import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Loader2 } from "lucide-react";

import { useDebounced } from "../lib/debounce";
import { controlClasses } from "./ui";
import { listarSocios, type SocioRead } from "../features/socios/api";

/**
 * Autocomplete de socios con búsqueda server-side (nombre / RUT / N° socio,
 * el backend normaliza el RUT). Al elegir un resultado invoca `onSelect` con
 * el `SocioRead` completo — el consumidor decide qué campos autocompletar.
 *
 * Pensado para reemplazar los campos de texto libre donde hoy se re-escribe
 * nombre/RUT de un socio que ya vive en el padrón (Atenciones, Multas,
 * Subsidios, arrendatario de Contrato, etc.).
 */
export function SocioAutocomplete({
  onSelect,
  onlySocios = false,
  label = "Buscar socio (nombre o RUT)",
  placeholder = "Ana Pérez · 12.345.678-9 · N° 42",
  autoFocus = false,
}: {
  onSelect: (socio: SocioRead) => void;
  onlySocios?: boolean;
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
}): JSX.Element {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const debounced = useDebounced(query.trim(), 250);
  const boxRef = useRef<HTMLDivElement>(null);

  const habilitado = debounced.length >= 2;
  const q = useQuery({
    queryKey: ["socios-autocomplete", debounced, onlySocios],
    queryFn: () =>
      listarSocios({ search: debounced, only_active: true, only_socios: onlySocios }),
    enabled: habilitado,
  });

  // Cierra el dropdown al hacer click fuera.
  useEffect(() => {
    function onClickAway(e: MouseEvent): void {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, []);

  const resultados = q.data ?? [];

  function elegir(s: SocioRead): void {
    onSelect(s);
    setQuery("");
    setOpen(false);
  }

  return (
    <div className="relative" ref={boxRef}>
      <label className="mb-1 block text-sm font-medium text-neutral-700">
        {label}
      </label>
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-600"
          strokeWidth={1.5}
        />
        <input
          type="text"
          value={query}
          autoFocus={autoFocus}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className={`${controlClasses} pl-8`}
          autoComplete="off"
        />
        {q.isFetching && (
          <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-neutral-600" />
        )}
      </div>

      {open && habilitado && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-neutral-200 bg-white shadow-lg">
          {q.isFetching && resultados.length === 0 ? (
            <div className="px-3 py-2 text-sm text-neutral-600">Buscando…</div>
          ) : resultados.length === 0 ? (
            <div className="px-3 py-2 text-sm text-neutral-600">
              Sin coincidencias en el padrón.
            </div>
          ) : (
            resultados.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => elegir(s)}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-brand-50"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-neutral-800">
                    {s.nombre}
                  </span>
                  <span className="block truncate text-xs text-neutral-600">
                    {s.rut}
                    {s.numero_socio != null ? ` · N° ${s.numero_socio}` : ""}
                    {!s.es_socio ? " · Cliente" : ""}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
